/**
 * PDP Sticky Footer Component
 * Shows after product is added to cart on PDP. Hides when drawers are open.
 */

import { Component } from "./component.js";
import { ThemeEvents } from "./events.js";
import { getCart, findGiftForParent } from "./pyp-gift-manager.js";

const formatPrice = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    cents / 100,
  );

class GRPDPStickyFooter extends Component {
  /** @type {string | null} */
  #lastVariantId = null;

  /** @type {boolean} */
  #hasAddedProduct = false;

  /** @type {{ showClaimGift: boolean, itemKey: string | null, sellingPlanId: string | null, quantity: number } | null} */
  #ctaState = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #refreshTimer = null;

  connectedCallback() {
    super.connectedCallback();

    // Show footer after cart add (but wait for drawer to close first)
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // Track drawer state via dialog toggle events on the document
    document.addEventListener("toggle", this.#onDialogToggle, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener("toggle", this.#onDialogToggle, true);
  }

  #onCartUpdate = async (event) => {
    // Only react to product-form adds on product pages
    const source = event.detail?.data?.source;
    if (source !== "product-form-component") return;
    if (!window.location.pathname.includes("/products/")) return;
    if (event.detail?.data?.didError) return;

    const variantId = event.detail?.data?.variantId || event.detail?.sourceId;
    if (!variantId) return;

    this.#lastVariantId = String(variantId);
    this.#hasAddedProduct = true;

    // Schedule a refresh - the dialog toggle will also trigger refresh,
    // but we need to handle cases where no drawer opens
    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    this.#refreshTimer = setTimeout(() => this.#refresh(), 100);
  };

  #onDialogToggle = () => {
    // Debounce: clear any pending refresh before scheduling a new one
    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    this.#refreshTimer = setTimeout(() => this.#refresh(), 50);
  };

  async #refresh() {
    if (!this.#hasAddedProduct) return;

    const isDrawerOpen = Boolean(document.querySelector("dialog[open]"));

    // Hide if drawer is open
    if (isDrawerOpen) {
      this.#hide();
      return;
    }

    // Fetch fresh cart data
    const cart = await getCart({ force: true });
    if (!cart?.items?.length) {
      this.#hide();
      return;
    }

    // Find the last added item
    const item = cart.items.find(
      (i) =>
        String(i.variant_id) === this.#lastVariantId &&
        !i.properties?._is_pyp_gift,
    );

    if (!item) {
      this.#hide();
      return;
    }

    // Update cart total
    this.#updateTotal(cart.total_price);

    // Determine CTA: "Claim Free Gift" if subscription without gift, else "Select Add-Ons"
    const hasSellingPlan = Boolean(item.selling_plan_allocation);
    const hasGift = item.key
      ? Boolean(findGiftForParent(cart, item.key))
      : false;
    const showClaimGift = hasSellingPlan && !hasGift;

    this.#updateCTA(
      showClaimGift,
      item.key,
      item.selling_plan_allocation?.selling_plan?.id,
      item.quantity,
    );
    this.#show();
  }

  #updateTotal(cents) {
    const el = this.refs.cartTotal;
    if (el) el.textContent = formatPrice(cents);
  }

  #updateCTA(showClaimGift, itemKey, sellingPlanId, quantity) {
    const label = showClaimGift
      ? this.dataset.ctaSubscription || "Claim Free Gift"
      : this.dataset.ctaOneTime || "Select Add-Ons";

    const { ctaText, ctaTextMobile } = this.refs;
    if (ctaText) ctaText.textContent = label;
    if (ctaTextMobile) ctaTextMobile.textContent = label;

    // Store for handleCTA
    this.#ctaState = { showClaimGift, itemKey, sellingPlanId, quantity };
  }

  #show() {
    const bar = this.refs.stickyBar;
    if (bar) {
      bar.classList.remove("hidden");
      bar.setAttribute("aria-hidden", "false");
    }
  }

  #hide() {
    const bar = this.refs.stickyBar;
    if (bar) {
      bar.classList.add("hidden");
      bar.setAttribute("aria-hidden", "true");
    }
  }

  handleCTA() {
    const state = this.#ctaState;
    if (state?.showClaimGift && state.itemKey) {
      // Open gift selection drawer
      const drawer = document.querySelector("gift-selection-drawer");
      if (
        drawer &&
        typeof (/** @type {any} */ (drawer).showSelectionMode) === "function"
      ) {
        /** @type {any} */ (drawer).showSelectionMode({
          parentItemId: state.itemKey,
          parentItemKey: state.itemKey,
          parentSellingPlanId: state.sellingPlanId,
          parentQuantity: state.quantity,
        });
        return;
      }
    }
    // Default: go to addons
    window.location.assign(
      this.dataset.addonsUrl || "/collections/pre-checkout-add-ons",
    );
  }
}

if (!customElements.get("gr-pdp-sticky-footer")) {
  customElements.define("gr-pdp-sticky-footer", GRPDPStickyFooter);
}
